import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fmtDate, dueClass } from "@/lib/api";
import { Truck, Users, AlertTriangle, FileWarning, Route, Printer } from "lucide-react";

const KPI = ({ icon: Icon, label, value, sub, accent, testid }) => (
  <div data-testid={testid} className="border border-border bg-card p-5 h-full">
    <div className="flex items-center justify-between mb-4">
      <span className="uppercase text-[10px] tracking-[0.25em] font-bold text-muted-foreground">{label}</span>
      <Icon size={16} className={accent || "text-muted-foreground"} />
    </div>
    <div className={`font-heading font-black text-4xl tracking-tighter ${accent || "text-foreground"}`}>{value}</div>
    {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
  </div>
);

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [vehicles, setVehicles] = useState([]);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/reminders").then((r) => setReminders(r.data)).catch(() => {});
    api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
  }, []);

  return (
    <div className="p-8" data-testid="dashboard-page">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter">DASHBOARD</h1>
          <p className="text-muted-foreground text-sm mt-1">Fleet status — {new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</p>
        </div>
        <button
          data-testid="weekly-report-button"
          onClick={() => window.open("/weekly-report", "_blank")}
          className="inline-flex items-center gap-2 border border-border px-4 h-10 text-xs uppercase tracking-widest font-bold hover:bg-muted transition-colors duration-150"
        >
          <Printer size={14} /> Weekly report
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <KPI testid="kpi-vehicles" icon={Truck} label="Fleet size" value={stats?.vehicles ?? "—"} sub="vehicles" />
        <KPI testid="kpi-drivers" icon={Users} label="Drivers" value={stats?.drivers ?? "—"} sub="active" />
        <KPI testid="kpi-alerts" icon={AlertTriangle} label="Alerts" value={stats?.alerts ?? "—"} sub={`${stats?.expired ?? 0} expired`} accent={stats?.alerts > 0 ? "text-amber-600 dark:text-[#FFD60A]" : undefined} />
        <KPI testid="kpi-fines" icon={FileWarning} label="Unpaid fines" value={stats?.unpaid_fines ?? "—"} sub={`£${stats?.unpaid_fines_total ?? 0}`} accent={stats?.unpaid_fines > 0 ? "text-[#FF3B30]" : undefined} />
        <KPI testid="kpi-routes" icon={Route} label="Routes today" value={stats?.routes_today ?? "—"} sub="planned" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 border border-border bg-card">
          <div className="px-5 py-4 border-b border-border flex items-center justify-between">
            <span className="uppercase text-xs tracking-[0.2em] font-bold text-muted-foreground">Fleet compliance</span>
            <Link to="/vehicles" className="text-xs text-[#FF3B30] hover:underline" data-testid="dashboard-view-vehicles-link">View all →</Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-border">
                  {["Reg", "Driver", "MOT", "Tax", "Service", "Tail lift", "Fridge"].map((h) => (
                    <th key={h} className="px-5 py-2.5 uppercase text-[10px] tracking-[0.2em] font-bold text-muted-foreground">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {vehicles.slice(0, 10).map((v) => (
                  <tr key={v.id} className="border-b border-border/60 hover:bg-muted/60">
                    <td className="px-5 py-2.5">
                      <Link to={`/vehicles/${v.id}`} className="font-mono font-bold text-foreground hover:text-[#FF3B30]">{v.registration}</Link>
                    </td>
                    <td className="px-5 py-2.5 text-muted-foreground">{v.driver_name || "—"}</td>
                    <td className={`px-5 py-2.5 ${dueClass(v.mot_expiry)}`}>{fmtDate(v.mot_expiry)}</td>
                    <td className={`px-5 py-2.5 ${dueClass(v.tax_due)}`}>{fmtDate(v.tax_due)}</td>
                    <td className={`px-5 py-2.5 ${dueClass(v.service_due)}`}>{fmtDate(v.service_due)}</td>
                    <td className={`px-5 py-2.5 ${v.has_tail_lift ? dueClass(v.tail_lift_expiry) : "text-muted-foreground/40"}`}>{v.has_tail_lift ? fmtDate(v.tail_lift_expiry) : "N/A"}</td>
                    <td className={`px-5 py-2.5 ${v.has_fridge ? dueClass(v.fridge_expiry) : "text-muted-foreground/40"}`}>{v.has_fridge ? fmtDate(v.fridge_expiry) : "N/A"}</td>
                  </tr>
                ))}
                {vehicles.length === 0 && (
                  <tr><td colSpan={7} className="px-5 py-10 text-center text-muted-foreground/70">No vehicles yet — add your first vehicle.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="border border-border bg-card" data-testid="reminders-panel">
          <div className="px-5 py-4 border-b border-border">
            <span className="uppercase text-xs tracking-[0.2em] font-bold text-muted-foreground">Reminders — next 7 days</span>
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            {reminders.length === 0 && <div className="px-5 py-10 text-center text-muted-foreground/70 text-sm">All clear. No expiries within 7 days.</div>}
            {reminders.map((r, i) => (
              <Link
                to={r.vehicle_id ? `/vehicles/${r.vehicle_id}` : "/insurance"}
                key={i}
                data-testid={`reminder-item-${i}`}
                className={`block px-5 py-3 border-b border-border/60 hover:bg-muted/60 border-l-2 ${r.severity === "expired" ? "border-l-[#FF3B30]" : "border-l-amber-500 dark:border-l-[#FFD60A]"}`}
              >
                <div className="flex justify-between items-center">
                  <span className="font-mono font-bold text-sm">{r.registration}</span>
                  <span className={`text-[10px] uppercase tracking-widest font-bold ${r.severity === "expired" ? "text-[#FF3B30]" : "text-amber-600 dark:text-[#FFD60A]"}`}>
                    {r.category === "recall" ? "RECALL" : r.days_left < 0 ? `${-r.days_left}d overdue` : r.days_left === 0 ? "today" : `${r.days_left}d left`}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">{r.category === "recall" ? r.type : <>{r.type} · due {fmtDate(r.due_date)}</>}</div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
